/**
 * RichTextEditor — full WYSIWYG used by the admin News editor.
 *
 * Features:
 *   • Marks: bold, italic, underline, strike, highlight
 *   • Text color (palette swatches)
 *   • Headings H2/H3
 *   • Lists (bullet, ordered)
 *   • Block quote, code block (for source code), inline code
 *   • Text alignment (left, center, right)
 *   • Links (URL prompt)
 *   • Images via file upload (POST /api/news/upload-image returns {url})
 *   • Undo / Redo
 *
 * Output: sanitized HTML stored in news.body_html and re-rendered via
 * the <SafeHtml /> component (DOMPurify + html-react-parser, no raw
 * HTML injection sink) in the client NewsBell drawer.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import Highlight from '@tiptap/extension-highlight';
import {
  Bold, Italic, Strikethrough, UnderlineIcon, List, ListOrdered, Heading2, Heading3,
  Quote, Link as LinkIcon, Undo2, Redo2, Code, Image as ImageIcon, Highlighter,
  AlignLeft, AlignCenter, AlignRight, Palette, Loader2, Code2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Cleanly chosen palette — covers most marketing/news needs without scaring users.
const COLORS = [
  '#0f172a', '#475569', '#94a3b8',           // slate
  '#dc2626', '#f97316', '#facc15',           // warm
  '#16a34a', '#0891b2', '#2563eb', '#7c3aed', // cool
];

export default function RichTextEditor({ value = '', onChange, placeholder = 'Write here…', token }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [showColors, setShowColors] = useState(false);
  // Source-mode (raw HTML) — kept at the top to satisfy rules-of-hooks.
  const [sourceMode, setSourceMode] = useState(false);
  const [sourceValue, setSourceValue] = useState('');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Underline,
      Highlight.configure({ multicolor: false }),
      TextStyle,
      Color,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { class: 'text-cyan-600 underline' } }),
      Image.configure({ inline: false, allowBase64: false, HTMLAttributes: { class: 'rounded-lg max-w-full my-3' } }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
    editorProps: {
      attributes: {
        class: 'prose max-w-none min-h-[260px] px-4 py-3 focus:outline-none news-editor-content',
        'data-testid': 'news-editor-area',
      },
    },
  });

  useEffect(() => {
    if (!editor) return;
    if (editor.getHTML() !== value) editor.commands.setContent(value || '', false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return <div className="h-[320px] bg-slate-50 rounded animate-pulse" />;

  const Btn = ({ active, onClick, title, children, testid }) => (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      title={title}
      className={`p-1.5 rounded hover:bg-slate-200 transition-colors ${active ? 'bg-slate-200 text-cyan-700' : 'text-slate-600'}`}
    >
      {children}
    </button>
  );

  const onLink = () => {
    const prev = editor.getAttributes('link').href;
    const url = window.prompt('Link URL', prev || 'https://');
    if (url === null) return;
    if (url === '') { editor.chain().focus().extendMarkRange('link').unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const pickImage = () => fileInputRef.current?.click();

  const onImageSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API_URL}/api/news/upload-image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || `HTTP ${r.status}`);
      // Backend returns a relative path like "/api/news/image/<x>.png".
      // Prepend the backend origin so the <img> in the published news works
      // for end users hitting the same external URL.
      const absolute = data.url?.startsWith('http') ? data.url : `${API_URL}${data.url}`;
      editor.chain().focus().setImage({ src: absolute, alt: file.name }).run();
    } catch (err) {
      alert(`Image upload failed: ${err.message || err}`);
    } finally { setUploading(false); }
  };

  const setColor = (c) => {
    editor.chain().focus().setColor(c).run();
    setShowColors(false);
  };
  const clearColor = () => {
    editor.chain().focus().unsetColor().run();
    setShowColors(false);
  };

  // For the "source view" toggle — let admins paste raw HTML.
  const enterSourceMode = () => { setSourceValue(editor.getHTML()); setSourceMode(true); };
  const exitSourceMode = () => {
    editor.commands.setContent(sourceValue || '<p></p>', false);
    onChange?.(sourceValue);
    setSourceMode(false);
  };

  return (
    <div className="border border-slate-300 rounded-lg overflow-visible bg-white" data-testid="news-rich-editor">
      <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 border-b border-slate-200 bg-slate-50 sticky top-0 z-10">
        {/* Marks */}
        <Btn active={editor.isActive('bold')}      onClick={() => editor.chain().focus().toggleBold().run()}      title="Bold"      testid="news-edit-bold"><Bold className="w-4 h-4" /></Btn>
        <Btn active={editor.isActive('italic')}    onClick={() => editor.chain().focus().toggleItalic().run()}    title="Italic"    testid="news-edit-italic"><Italic className="w-4 h-4" /></Btn>
        <Btn active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Underline" testid="news-edit-underline"><UnderlineIcon className="w-4 h-4" /></Btn>
        <Btn active={editor.isActive('strike')}    onClick={() => editor.chain().focus().toggleStrike().run()}    title="Strike"><Strikethrough className="w-4 h-4" /></Btn>
        <Btn active={editor.isActive('highlight')} onClick={() => editor.chain().focus().toggleHighlight().run()} title="Highlight" testid="news-edit-highlight"><Highlighter className="w-4 h-4" /></Btn>

        {/* Color picker */}
        <div className="relative">
          <Btn active={showColors} onClick={() => setShowColors((s) => !s)} title="Text color" testid="news-edit-color">
            <Palette className="w-4 h-4" />
          </Btn>
          {showColors && (
            <div className="absolute top-9 left-0 z-20 bg-white border border-slate-200 rounded-lg shadow-lg p-2 w-44" data-testid="news-color-palette">
              <div className="grid grid-cols-5 gap-1.5">
                {COLORS.map((c) => (
                  <button key={c} type="button" onClick={() => setColor(c)} title={c}
                    data-testid={`news-color-swatch-${c.replace('#','')}`}
                    style={{ backgroundColor: c }}
                    className="w-7 h-7 rounded border border-slate-200 hover:scale-110 transition-transform" />
                ))}
              </div>
              <button type="button" onClick={clearColor} className="mt-2 w-full text-[11px] text-slate-500 hover:text-slate-700 underline">
                Clear color
              </button>
            </div>
          )}
        </div>

        <span className="w-px h-5 bg-slate-300 mx-1" />

        {/* Headings */}
        <Btn active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Heading 2"><Heading2 className="w-4 h-4" /></Btn>
        <Btn active={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Heading 3"><Heading3 className="w-4 h-4" /></Btn>

        <span className="w-px h-5 bg-slate-300 mx-1" />

        {/* Alignment */}
        <Btn active={editor.isActive({ textAlign: 'left' })}   onClick={() => editor.chain().focus().setTextAlign('left').run()}   title="Align left"><AlignLeft className="w-4 h-4" /></Btn>
        <Btn active={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()} title="Center"><AlignCenter className="w-4 h-4" /></Btn>
        <Btn active={editor.isActive({ textAlign: 'right' })}  onClick={() => editor.chain().focus().setTextAlign('right').run()}  title="Align right"><AlignRight className="w-4 h-4" /></Btn>

        <span className="w-px h-5 bg-slate-300 mx-1" />

        {/* Lists / Quote / Code */}
        <Btn active={editor.isActive('bulletList')}  onClick={() => editor.chain().focus().toggleBulletList().run()}  title="Bullet list"><List className="w-4 h-4" /></Btn>
        <Btn active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Ordered list"><ListOrdered className="w-4 h-4" /></Btn>
        <Btn active={editor.isActive('blockquote')}  onClick={() => editor.chain().focus().toggleBlockquote().run()}  title="Quote"><Quote className="w-4 h-4" /></Btn>
        <Btn active={editor.isActive('codeBlock')}   onClick={() => editor.chain().focus().toggleCodeBlock().run()}   title="Code block (paste source code)" testid="news-edit-codeblock"><Code className="w-4 h-4" /></Btn>

        <span className="w-px h-5 bg-slate-300 mx-1" />

        {/* Link / Image / HTML source */}
        <Btn active={editor.isActive('link')} onClick={onLink} title="Insert link"><LinkIcon className="w-4 h-4" /></Btn>
        <Btn onClick={pickImage} title="Insert image" testid="news-edit-image">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
        </Btn>
        <Btn active={sourceMode} onClick={sourceMode ? exitSourceMode : enterSourceMode} title="Edit raw HTML / source code" testid="news-edit-source">
          <Code2 className="w-4 h-4" />
        </Btn>

        <span className="w-px h-5 bg-slate-300 mx-1" />

        {/* History */}
        <Btn onClick={() => editor.chain().focus().undo().run()} title="Undo"><Undo2 className="w-4 h-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().redo().run()} title="Redo"><Redo2 className="w-4 h-4" /></Btn>

        <input ref={fileInputRef} type="file" accept="image/*" onChange={onImageSelected} className="hidden" data-testid="news-edit-image-input" />
      </div>

      {sourceMode ? (
        <textarea
          value={sourceValue}
          onChange={(e) => setSourceValue(e.target.value)}
          spellCheck={false}
          data-testid="news-source-textarea"
          className="w-full min-h-[260px] px-4 py-3 font-mono text-xs text-slate-800 focus:outline-none resize-y"
          placeholder="Paste/edit raw HTML here, then click the &lt;/&gt; button again to apply."
        />
      ) : (
        <EditorContent editor={editor} />
      )}
      {!sourceMode && !editor.getText() && (
        <div className="px-4 -mt-[240px] text-slate-400 text-sm italic pointer-events-none select-none">{placeholder}</div>
      )}
    </div>
  );
}
